import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { AuthPasswordInput, AuthButton } from "./AuthLayout";
import { changePassword } from "../api/auth";

export function ChangePasswordForm({ onSuccess }: { onSuccess: () => void }) {
    const { t } = useTranslation();

    const schema = z.object({
        old_password: z.string().min(1, t('auth.changePassword.errors.oldPasswordRequired')),
        new_password: z.string().min(6, t('auth.changePassword.errors.newPasswordMin')),
        confirm_password: z.string().min(6, t('auth.changePassword.errors.confirmPasswordMin')),
    }).refine((data) => data.new_password === data.confirm_password, {
        message: t('auth.changePassword.errors.passwordMismatch'),
        path: ["confirm_password"],
    });

    type FormData = z.infer<typeof schema>;

    const { register, handleSubmit, formState: { errors }, setError } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const [loading, setLoading] = useState(false);

    const onSubmit = async (data: FormData) => {
        setLoading(true);
        try {
            await changePassword({
                current_password: data.old_password,
                new_password: data.new_password,
                confirm_password: data.confirm_password
            });
            onSuccess();
        } catch (e: any) {
            const errorMessage = e.response?.data?.message || e.response?.data?.error?.message;
            if (errorMessage) {
                setError("root", { message: errorMessage });
            } else {
                setError("root", { message: t('auth.changePassword.errors.featureUnavailable') });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center">
            <h1 className="font-bold text-3xl text-[#338585] mb-12">{t('auth.changePassword.title')}</h1>

            {errors.root && (
                <div className="w-full mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {errors.root.message}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <AuthPasswordInput
                placeholder={t('auth.changePassword.oldPasswordPlaceholder')}
                {...register("old_password")}
                error={errors.old_password?.message}
            />
            <AuthPasswordInput
                placeholder={t('auth.changePassword.newPasswordPlaceholder')}
                {...register("new_password")}
                error={errors.new_password?.message}
            />
            <AuthPasswordInput
                placeholder={t('auth.changePassword.confirmPasswordPlaceholder')}
                {...register("confirm_password")}
                error={errors.confirm_password?.message}
            />

            <AuthButton disabled={loading}>
                {loading ? t('auth.changePassword.changing') : t('auth.changePassword.changeButton')}
            </AuthButton>
        </form>
    );
}
